<script setup lang="ts">
  import { useOrders } from '@/composition/use-orders';
  import { OrderType, OrderMode } from '@/enums/OrderEnums';
  import { useOrdersStore } from '@/stores/orders.store';
  import { computed, nextTick, onBeforeMount, provide, ref, watch } from 'vue';
  import RestaurantPosTables from './pos/RestaurantPosTables.vue';
  import RestaurantPosNewOrders from './pos/RestaurantPosNewOrders.vue';
  import RestaurantPosOrders from './pos/RestaurantPosOrders.vue';
  import { useModal } from '@/composition/use-modal';
  import ModalPosNewOrder from '@/components/modals/pos/new-order/ModalPosNewOrder.vue';
  import RestaurantPosCallsDropdown from './pos/RestaurantPosCallsDropdown.vue';
  import V2ShellContextBar from '@/components/v2/shell/V2ShellContextBar.vue';
  import RestaurantPosPrintingDropdown from './pos/RestaurantPosPrintingDropdown.vue';
  import { orderMatchesSearch } from '@/utils/orders.utils';
  import { useMenuStore } from '@/composition/use-menu';
  import { useRestaurantStore } from '@/stores/restaurant.store';
  import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';
  import RestaurantPosNewOrdersDropdown from './pos/RestaurantPosNewOrdersDropdown.vue';

  const modals = useModal();
  const ordersHelper = useOrders();
  const ordersStore = useOrdersStore();
  const restaurantStore = useRestaurantStore();
  const menuStore = useMenuStore();
  const search = ref<string | null>(null);
  const breakpoints = useBreakpoints(breakpointsTailwind);

  const isLargeScreen = breakpoints.greaterOrEqual('lg');

  provide('search', search);

  const pendingOrdersByType = computed(() => {
    if (!ordersStore.pendingOrdersByType || !search.value?.trim?.())
      return ordersStore.pendingOrdersByType;

    return {
      [OrderType.Seat]: ordersStore.pendingOrdersByType[
        OrderType.Seat
      ]?.filter?.((o) => orderMatchesSearch(o, search.value)),
      [OrderType.Away]: ordersStore.pendingOrdersByType[
        OrderType.Away
      ]?.filter?.((o) => orderMatchesSearch(o, search.value)),
      [OrderType.Delivery]: ordersStore.pendingOrdersByType[
        OrderType.Delivery
      ]?.filter?.((o) => orderMatchesSearch(o, search.value)),
    };
  });

  // A la mesa, Take away, Delivery
  const tabs = computed(() => {
    const t = [
      {
        id: OrderType.Seat,
        label: 'A la mesa',
        icon: 'mdi-chair-rolling',
        orderCount: pendingOrdersByType.value?.[OrderType.Seat]?.length,
        disabled: !menuStore.availableOrderTypes?.some(
          (type) => type.type === OrderType.Seat
        ),
      },
      {
        id: OrderType.Away,
        label: 'Take away',
        icon: 'mdi-shopping',
        orderCount: pendingOrdersByType.value?.[OrderType.Away]?.length,
        disabled: !menuStore.availableOrderTypes?.some(
          (type) => type.type === OrderType.Away
        ),
      },
      {
        id: OrderType.Delivery,
        label: 'Delivery',
        icon: 'mdi-truck-delivery',
        orderCount: pendingOrdersByType.value?.[OrderType.Delivery]?.length,
        disabled: !menuStore.availableOrderTypes?.some(
          (type) => type.type === OrderType.Delivery
        ),
      },
    ];

    return t;
  });

  const selectedTabIndex = ref(0);
  const selectedTab = computed(() => tabs.value[selectedTabIndex.value]);

  watch(
    () => menuStore.selectedOrderType,
    (type) => {
      if (!type || type === selectedTab.value.id) return;
      const index = tabs.value.findIndex((tab) => tab.id === type);
      if (index !== -1) selectedTabIndex.value = index;
    },
    { immediate: true }
  );

  watch(
    selectedTab,
    (tab) => {
      menuStore.setSelectedOrderType(tab.id);
    },
    { immediate: true }
  );

  const filteredPendingOrders = computed(() => {
    return pendingOrdersByType.value?.[selectedTab.value.id];
  });

  function openNewOrderModal() {
    if (isWhatsAppMode.value) {
      return;
    }

    modals.show(
      ModalPosNewOrder,
      {},
      {
        name: 'pos-new-order',
        adaptive: true,
        classes: 'dialog--height-limit',
        height: 800,
        scrollable: true,
        maxWidth: window.innerWidth * 0.95,
        width: 1280,
        transition: 'none',
        clickToClose: false,
      }
    );
  }

  const searchInput = ref<any | null>(null);

  function toggleSearch() {
    if (search.value === null) {
      search.value = '';
      nextTick(() => {
        searchInput.value.focus();
      });
    } else {
      search.value = null;
    }
  }

  // Branch and caja of this page (the app shell does not show them here).
  const showBranchSelector = computed(
    () =>
      restaurantStore.effectiveTier > 1 &&
      (restaurantStore.branches?.length ?? 0) > 1
  );
  const showCajaSelector = computed(
    () =>
      restaurantStore.effectiveTier > 0 &&
      !!restaurantStore.selectedBranch?.settingUseCajas &&
      !!restaurantStore.cajas
  );

  const isWhatsAppMode = computed(() => {
    return restaurantStore.selectedBranch?.ordersMode === OrderMode.WhatsApp;
  });
</script>

<template>
  <div class="pos-page">
    <div class="pos-page--inner">
      <V2ShellContextBar
        :show-branch="showBranchSelector"
        :show-caja="showCajaSelector"
      />
      <div v-if="!isLargeScreen" class="pos__header">
        <RestaurantPosNewOrdersDropdown />
        <RestaurantPosCallsDropdown />
        <RestaurantPosPrintingDropdown />
      </div>
      <div class="pos__main">
        <v-card class="pos__pending dashed-box">
          <v-card-title>
            <div class="title-container">
              <div class="title-top">
                <span>Pedidos en proceso</span>
                <v-btn
                  v-if="search === null"
                  small
                  color="primary"
                  text
                  @click="toggleSearch"
                >
                  <v-icon small>mdi-magnify</v-icon>
                  <span>Buscar</span>
                </v-btn>
              </div>
              <v-text-field
                ref="searchInput"
                class="search-input"
                v-show="search !== null"
                v-model="search"
                clearable
                dense
                outlined
                rounded
                hide-details
                prepend-inner-icon="mdi-magnify"
                placeholder="Buscar"
                @click:clear="toggleSearch"
                @blur="search?.trim() === '' && toggleSearch()"
              />
            </div>
            <div class="spacer"></div>
            <template v-if="!isWhatsAppMode">
              <v-btn
                class="new-order"
                small
                color="green"
                depressed
                dark
                @click="openNewOrderModal"
              >
                <v-icon small class="pr-1">mdi-plus</v-icon>
                Nuevo pedido
              </v-btn>
            </template>
            <template v-else>
              <v-card
                class="whatsapp-warning"
                flat
                outlined
                color="orange lighten-5"
              >
                <v-card-text class="pa-3">
                  <div class="warning-content">
                    <v-icon color="orange" small class="pr-2"
                      >mdi-information</v-icon
                    >
                    <div class="warning-text">
                      <span class="warning-message"
                        >Creación manual de pedidos deshabilitada en modo
                        WhatsApp</span
                      >
                      <v-btn
                        :to="`/${restaurantStore.restaurant.stringId}/settings?tab=orders`"
                        color="orange"
                        text
                        x-small
                        class="pa-0 ml-2"
                      >
                        <v-icon x-small class="pr-1">mdi-cog</v-icon>
                        Configurar
                      </v-btn>
                    </div>
                  </div>
                </v-card-text>
              </v-card>
            </template>
          </v-card-title>
          <v-card-text>
            <div class="tabs">
              <v-btn
                v-for="(tab, index) in tabs"
                :key="tab.id"
                @click="selectedTabIndex = index"
                class="tab"
                :class="{ active: selectedTabIndex === index }"
                small
                :color="selectedTabIndex === index ? 'primary' : undefined"
                :disabled="tab.disabled"
              >
                <v-icon class="pr-1" small>{{ tab.icon }}</v-icon>
                <span>{{ tab.label }}</span>
                <v-chip
                  v-if="tab.orderCount"
                  x-small
                  color="primary"
                  class="tw-ml-1"
                  dark
                >
                  {{ tab.orderCount }}
                </v-chip>
              </v-btn>
              <!--settings-->
              <v-btn
                small
                color="grey"
                text
                icon
                :to="`/${restaurantStore.restaurant.stringId}/settings?tab=orders`"
              >
                <v-icon small>mdi-cog</v-icon>
              </v-btn>
            </div>
            <div class="content">
              <template v-if="selectedTab.id === OrderType.Seat">
                <RestaurantPosTables />
              </template>
              <template v-else>
                <RestaurantPosOrders :orders="filteredPendingOrders" />
              </template>
            </div>
          </v-card-text>
        </v-card>
        <div v-if="isLargeScreen" class="pos-new-container">
          <div class="pos-new-header dashed-box">
            <RestaurantPosCallsDropdown />
            <RestaurantPosPrintingDropdown />
          </div>
          <v-card class="pos__new dashed-box">
            <v-card-title>Pedidos nuevos</v-card-title>
            <v-card-text>
              <RestaurantPosNewOrders />
            </v-card-text>
          </v-card>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
  .pos-page {
    --nav-height: 48px;
    @apply tw-flex tw-justify-center;
    height: calc(100vh - var(--nav-height));
    // dvh must live in @supports: var()-containing declarations skip parse-time
    // validation, so a plain dvh line would kill the vh fallback on Chrome < 108.
    @supports (height: 100dvh) {
      height: calc(100dvh - var(--nav-height));
    }

    .pos-page--inner {
      @apply tw-w-full tw-flex tw-flex-col;

      .pos__header {
        @apply tw-flex tw-justify-end tw-space-x-1.5 tw-mt-2 tw-mx-4;
        @apply tw-bg-white tw-rounded-lg tw-p-1.5;
      }

      .pos__main {
        @apply tw-flex-1 tw-min-h-1;
        @apply tw-flex tw-space-x-2 tw-flex-nowrap tw-px-4 tw-pb-2 tw-py-2;

        .empty {
          @apply tw-flex tw-flex-col tw-items-center tw-space-y-1 tw-text-gray-400 tw-p-4;
        }

        .pos__pending {
          @apply tw-flex-1 tw-shadow-none tw-rounded-lg;
          @apply tw-flex-col;
          display: flex !important;
          min-width: 1px;

          .title-container {
            .title-top {
              @apply tw-flex tw-items-center tw-space-x-2;
            }

            ::v-deep(.v-input__icon--clear) {
              opacity: 1 !important;
            }
          }

          .tabs {
            @apply tw-flex tw-gap-2 tw-flex-wrap;

            .tab {
              @apply tw-shadow-none tw-rounded-lg;
            }
          }

          ::v-deep(.v-card__text) {
            @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto tw-flex tw-flex-col tw-space-y-4;
          }

          .content {
            @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;
          }

          .whatsapp-warning {
            @apply tw-rounded-lg;

            .warning-content {
              @apply tw-flex tw-items-start tw-space-x-0;

              .warning-text {
                @apply tw-flex tw-flex-col tw-space-y-1 sm:tw-flex-row sm:tw-items-center sm:tw-space-y-0;

                .warning-message {
                  @apply tw-text-sm tw-font-medium tw-text-orange-800;
                }
              }
            }
          }
        }

        .pos-new-container {
          @apply tw-flex tw-flex-col tw-space-y-2;

          .pos-new-header {
            @apply tw-flex tw-justify-start tw-space-x-1.5;
            @apply tw-bg-white tw-rounded-lg tw-p-1.5;
          }

          .pos__new {
            @apply tw-w-[22rem] xl:tw-w-[25rem] tw-shadow-none tw-rounded-lg;
            @apply tw-flex-col tw-flex-1 tw-min-h-1;
            display: flex !important;

            ::v-deep(.v-card__text) {
              @apply tw-flex-1 tw-min-h-1 tw-overflow-y-auto;
            }
          }
        }
      }
    }
  }

  .dashed-box {
    @apply tw-shadow-sm;
  }
</style>
